import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    q: "What is Operational AI Security?",
    a: "Operational AI Security is the practice of securing AI systems already moving through real business operations. It looks at the AI application, agent behavior, data flows, tool access, permissions, vendors, workflows, controls, logging, and evidence, not just the model.",
  },
  {
    q: "How is this different from AI security testing?",
    a: "AI security testing often focuses on model behavior, prompts, or isolated abuse cases. ArqeraAI evaluates the broader operational surface: what the AI can access, what it can do, what could fail, what needs remediation, and what evidence exists to prove the risk is controlled.",
  },
  {
    q: "Why are AI agents different from normal AI applications?",
    a: "AI agents can retrieve data, use tools, call APIs, update records, trigger workflows, and influence operational decisions. That makes their risk profile larger than a chatbot or static AI feature because agent failure can affect business systems directly.",
  },
  {
    q: "Do you only work with regulated companies?",
    a: "Regulated and high-stakes organizations are the strongest fit because AI failure has real business, legal, operational, and reputational consequences. That includes healthcare, financial services, legal, insurance, manufacturing, SaaS, AI-native companies, and PE-backed operators.",
  },
  {
    q: "What do we receive at the end?",
    a: "Depending on scope, clients receive prioritized findings, an AI and workflow exposure map, remediation roadmap, control validation log, executive decision brief, and evidence package suitable for external or leadership review.",
  },
  {
    q: "Do you only provide recommendations?",
    a: "No. ArqeraAI helps move from assessment to remediation and validation. We identify what is exposed, help close the highest-risk gaps, test the fixes, and produce evidence that the risk has been reduced.",
  },
  {
    q: "What is your pricing model?",
    a: "Engagements are fixed-scope and priced around the value of the outcome being created: risk reduced, exposure closed, controls validated, evidence produced, and executive decisions clarified. After a readiness conversation, ArqeraAI provides written options with clear scope, outcomes, timeline, and investment.",
  },
  {
    q: "Can you help if we are under regulatory, audit, customer, or insurance pressure?",
    a: "Yes. These are often the situations where evidence matters most. ArqeraAI helps clarify what is exposed, what needs remediation, what can be validated, and what documentation is needed for the review process.",
  },
];

function Item({ q, a, open, onClick }: { q: string; a: string; open: boolean; onClick: () => void }) {
  return (
    <div className="border-b border-arq-border">
      <button
        onClick={onClick}
        className="w-full text-left py-5 flex items-start justify-between gap-6 group"
      >
        <span className="font-body text-[16px] font-medium text-arq-text group-hover:text-arq-blue transition-colors">
          {q}
        </span>
        <span className="text-arq-blue flex-shrink-0 mt-1 transition-transform duration-200">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      {open && (
        <div className="pb-5 pr-10 text-arq-muted text-[15px] leading-[1.7]">{a}</div>
      )}
    </div>
  );
}

export default function ArqFAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(4);
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const toggle = (i: number) => setOpenIdx(openIdx === i ? null : i);

  return (
    <section id="faq" className="bg-arq-surface1 py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center mb-16">
          <div className="arq-label mb-6">Common Questions</div>
          <h2 className="font-display font-semibold text-arq-text" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            What to Expect
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12">
          <div>
            {left.map((f, i) => (
              <Item key={i} q={f.q} a={f.a} open={openIdx === i} onClick={() => toggle(i)} />
            ))}
          </div>
          <div>
            {right.map((f, i) => (
              <Item key={i + half} q={f.q} a={f.a} open={openIdx === i + half} onClick={() => toggle(i + half)} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
